import React from 'react';
import PropTypes from 'prop-types';
import { Card, Flex, Heading, Button } from 'core/components';

/**
 * Reusable wrapper component for Kaizen tool results.
 * Provides consistent styling and header with close button.
 */
const ToolResultCard = ({
  tool,
  onCloseToolResult,
  className,
  title,
  children,
  minHeight = 600,
  height = '100%',
  isInlineMode = false,
  headerActions = null,
  headerLevel = 5,
  ...cardProps
}) => {
  const displayTitle = title || tool.title || 'Tool Result';
  const effectiveHeaderLevel = isInlineMode ? 6 : headerLevel;

  // in Copilot mode, we don't show the close button because clicking the ToolResultCard itself will collapse it.
  const showCloseButton = !isInlineMode;

  // in Copilot mode, the title is hidden because the Card already shows the title.
  const showTitle = !isInlineMode;

  const borderRadius = isInlineMode ? '0 0 8px 8px' : '8px';

  // if there is no title, we right-align the header actions
  const justifyContent = showTitle ? 'space-between' : 'flex-end';

  return (
    <Card
      p={0}
      className={className}
      display="flex"
      flexDirection="column"
      gap={2}
      height={height}
      minHeight={minHeight}
      maxHeight={isInlineMode ? 500 : 'auto'}
      overflow="hidden"
      borderRadius={borderRadius}
      {...cardProps}
    >
      <Flex alignItems="center" justifyContent={justifyContent} p={2} pb={0}>
        {showTitle && (
          <Heading level={effectiveHeaderLevel} m={0} flex={1}>
            {displayTitle}
          </Heading>
        )}
        <Flex alignItems="center" gap={1}>
          {headerActions}
          {showCloseButton && (
            <Button text="Close" small icon="cross" onClick={() => onCloseToolResult(tool)} color="muted" minimal />
          )}
        </Flex>
      </Flex>
      {children}
    </Card>
  );
};

ToolResultCard.propTypes = {
  /** Tool object containing result data */
  tool: PropTypes.object.isRequired,
  /** Callback function to close the tool result */
  onCloseToolResult: PropTypes.func.isRequired,
  /** Optional className for the Card component */
  className: PropTypes.string,
  /** Optional title to override tool.title */
  title: PropTypes.string,
  /** Child components to render inside the card */
  children: PropTypes.node.isRequired,
  /** Minimum height of the card (default: 600) */
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Height of the card (default: '100%') */
  height: PropTypes.string,
  /** Whether the tool result is displayed in inline mode */
  isInlineMode: PropTypes.bool,
  /** Optional additional actions to display in the header */
  headerActions: PropTypes.node,
  /** Heading level for the title (default: 5) */
  headerLevel: PropTypes.number
};

ToolResultCard.defaultProps = {
  className: undefined,
  title: undefined,
  minHeight: 600,
  height: '100%',
  isInlineMode: false,
  headerActions: null,
  headerLevel: 5
};

export default ToolResultCard;
